import sigunguTopologyJson from "../assets/sigungu.topo.gen.json";
import successionLedgerJson from "../assets/succession-2026-07-01.gen.json";

export const ADMIN_VERSION_ID = "v2026-08-24" as const;

export type SuccessionEdge = Readonly<{
  fromCode: string;
  fromName: string;
  toCode: string;
}>;

type TopologyGeometry = {
  properties?: {
    sigunguCode?: string;
    sidoCode?: string;
    name?: string;
    nameEn?: string;
  };
};

type SigunguTopology = {
  type: "Topology";
  objects: {
    sigungu: {
      type: "GeometryCollection";
      geometries: TopologyGeometry[];
    };
  };
};

type SuccessionLedger = {
  officialSuccessionCount: number;
  edges: Array<SuccessionEdge & { provenance?: string }>;
};

export const SIGUNGU_TOPOLOGY = sigunguTopologyJson as unknown as SigunguTopology;

export const SIGUNGU = Object.freeze(
  SIGUNGU_TOPOLOGY.objects.sigungu.geometries.map((geometry) => {
    const properties = geometry.properties;
    if (!properties?.sigunguCode || !properties.sidoCode || !properties.name) {
      throw new Error("sigungu topology has an incomplete identity row");
    }
    return Object.freeze({
      code: properties.sigunguCode,
      sidoCode: properties.sidoCode,
      name: properties.name,
      nameEn: properties.nameEn ?? null,
    });
  }),
);

const sigunguCodeSet = new Set(SIGUNGU.map((region) => region.code));
if (SIGUNGU.length !== 230 || sigunguCodeSet.size !== 230) {
  throw new Error(`sigungu topology must contain 230 unique rows, got ${SIGUNGU.length}/${sigunguCodeSet.size}`);
}

export function assertSuccessionFromCodeInjective(edges: readonly SuccessionEdge[]): void {
  const fromCodes = edges.map((edge) => edge.fromCode);
  if (new Set(fromCodes).size !== fromCodes.length) {
    throw new Error("succession fromCode is not injective");
  }
}

const successionLedger = successionLedgerJson as SuccessionLedger;
export const SUCCESSION_EDGES: readonly SuccessionEdge[] = Object.freeze(
  successionLedger.edges
    .filter((edge) => edge.provenance !== "product_identity_correction")
    .map(({ fromCode, fromName, toCode }) => Object.freeze({ fromCode, fromName, toCode })),
);

assertSuccessionFromCodeInjective(SUCCESSION_EDGES);
if (successionLedger.officialSuccessionCount !== 27 || SUCCESSION_EDGES.length !== 27) {
  throw new Error(`official succession ledger must contain 27 edges, got ${SUCCESSION_EDGES.length}`);
}
for (const edge of SUCCESSION_EDGES) {
  if (!sigunguCodeSet.has(edge.toCode)) {
    throw new Error(`succession target is not a current sigungu code: ${edge.toCode}`);
  }
}

export function applySuccession(
  code: string,
  edges: readonly SuccessionEdge[] = SUCCESSION_EDGES,
  warn: (message: string) => void = console.warn,
): string {
  assertSuccessionFromCodeInjective(edges);
  const successorByCode = new Map(edges.map((edge) => [edge.fromCode, edge.toCode]));
  let current = code;

  for (let step = 0; step < 3; step += 1) {
    const next = successorByCode.get(current);
    if (!next) return current;
    current = next;
  }

  if (successorByCode.has(current)) {
    warn(`succession chain exceeded 3 steps at ${current}`);
  }
  return current;
}
